import { useMemo } from 'react';

import useSWR, { useSWRConfig } from 'swr';

import { useCurrentProjectId } from '@adsk/alloy-provider';
import { DocsFileTypes } from '@adsk/docs-js-sdk';

import {
  createAttachmentUrl,
  getAttachmentType,
  hasPreview,
} from '../api/attachments';
import { useAttachmentsContext } from '../context/AttachmentsContext';

type UsePreviewReturnValue = {
  error: Error | undefined;
  preview: string | undefined;
  loading: boolean;
  type?: DocsFileTypes;
};

type UsePreviewProps = {
  attachmentId?: string;
};

const usePreview = ({
  attachmentId,
}: UsePreviewProps): UsePreviewReturnValue => {
  const { attachments } = useAttachmentsContext();
  const { cache } = useSWRConfig();
  const projectId = useCurrentProjectId();

  const attachment = useMemo(
    () =>
      attachments.find(
        (attachment) => attachment.attachmentId === attachmentId,
      ),
    [attachments, attachmentId],
  );

  const shouldHavePreview = useMemo(
    () => attachment && hasPreview(attachment),
    [attachment],
  );

  const swrKey = useMemo(() => `${attachmentId}_preview`, [attachmentId]);
  const shouldRevalidate = useMemo(
    () => !cache.get(swrKey) && shouldHavePreview,
    [cache, swrKey, shouldHavePreview],
  );

  const {
    error,
    data: preview,
    isValidating,
  } = useSWR(
    swrKey,
    () => {
      if (!attachment || !shouldHavePreview || !projectId) {
        return Promise.resolve(undefined);
      }

      //create Download url
      return createAttachmentUrl({
        projectId,
        fileUrn: attachment.tipVersionUrn,
        filename: attachment.fileName,
        contentType: 'application/octet-stream',
        contentDisposition: 'attachment',
        errorMessage: 'Failed to get download link',
      });
    },
    {
      revalidateOnMount: shouldRevalidate,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      refreshInterval: 0,
    },
  );

  return {
    error,
    loading: isValidating,
    preview,
    type: attachment && getAttachmentType(attachment.fileName),
  };
};

export default usePreview;
